import React, { useEffect, useId } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { closeModal, selectActiveModalData } from 'features/modal/modalSlice';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogActions, DialogContent, DialogTitle } from 'components/Dialog';
import { Box, Button, FormControl } from '@mui/material';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import Input from 'components/Input';
import { selectLocalUserId, selectUserById } from 'features/users/usersSlice';
import { PermissionTypes } from 'features/permissions/types';
import { usePermissions } from 'features/permissions/usePermissions';
import { renameUserRequested } from 'features/users/actions';
import { USER_NAME_MAX_LENGTH } from 'features/users/constants';

const renameUserSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'User name is required')
    .max(USER_NAME_MAX_LENGTH, `User name must be less than ${USER_NAME_MAX_LENGTH} characters`),
});

type RenameUserValues = z.infer<typeof renameUserSchema>;

const RenameUserModal = () => {
  const { t } = useTranslation('modals');
  const dispatch = useAppDispatch();

  const modalId = useId();

  const modalData = useAppSelector(selectActiveModalData);
  const userId = modalData.userId as string;

  const user = useAppSelector((state) => selectUserById(state, userId));
  const localUserId = useAppSelector(selectLocalUserId);

  const { hasPermissions } = usePermissions();
  const isRenamingSelf = userId === localUserId;

  const canRenameParticipant = hasPermissions(PermissionTypes.renameParticipants, {
    targetRole: user.role,
  });

  const canSelfRename = hasPermissions(PermissionTypes.selfRename);

  const {
    handleSubmit,
    formState: { errors, isValid },
    control,
  } = useForm<RenameUserValues>({
    resolver: zodResolver(renameUserSchema),
    mode: 'onChange',
    defaultValues: {
      name: user?.name || '',
    },
  });

  const hasPermission =
    (isRenamingSelf && canSelfRename) || (!isRenamingSelf && canRenameParticipant);

  const shouldClose = !hasPermission || !user;

  useEffect(() => {
    if (shouldClose) {
      dispatch(closeModal('renameUser'));
    }
  }, [shouldClose, dispatch]);

  const handleCancel = () => {
    dispatch(closeModal('renameUser'));
  };

  const handleRename: SubmitHandler<RenameUserValues> = (data) => {
    dispatch(
      renameUserRequested({
        id: userId,
        name: data.name,
        local: isRenamingSelf,
      })
    );
    dispatch(closeModal('renameUser'));
  };

  return (
    <Dialog
      open={!shouldClose}
      maxWidth="xs"
      aria-labelledby={`${modalId}-title`}
      onClose={handleCancel}
    >
      <DialogTitle id={`${modalId}-title`}>{t('rename_user.title')}</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            pt: 1,
          }}
        >
          <FormControl fullWidth error={!!errors.name}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  autoFocus
                  inputProps={{
                    'aria-label': t('rename_user.input_name_placeholder'),
                  }}
                  placeholder={t('rename_user.input_name_placeholder')}
                  autoComplete="off"
                />
              )}
            />
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button type="button" color="grayscale" onClick={handleCancel}>
          {t('rename_user.btn_cancel_label')}
        </Button>
        <Button type="button" onClick={handleSubmit(handleRename)} disabled={!isValid}>
          {t('rename_user.btn_ok_label')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RenameUserModal;
